import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { authenticate, confirm2FA, googleSSO } from "../store/auth/thunks";
import { useNavigate } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";

import '../styles/profile.css';

import {
  Flex,
  Heading,
  Input,
  Button,
  InputGroup,
  Stack,
  InputLeftElement,
  Text,
  chakra,
  Box,
  Spinner,
  useColorMode,
  Link,
  FormControl,
  InputRightElement,
  Center,
  HStack,
  Divider,
} from "@chakra-ui/react";
import { FaUserAlt, FaLock } from "react-icons/fa";
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import useToastNotification from "../hooks/useToastNotification";
import useAsyncState from "../hooks/useAsyncState";


const CFaUserAlt = chakra(FaUserAlt);
const CFaLock = chakra(FaLock);

export default function Login() {
  const showToast = useToastNotification();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.user);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const { loading: authLoading, success } = useAsyncState({
    onError: (info) => {
      if (info?.response?.data?.result) {
        showToast(info.response.data.result, { status: "error" });
        if (info.response.data.result === "5 Day Trial Period Expired, Payment Required") {
          setTimeout(() => {
            navigate("/payment", { state: { email } });
          }, 500);
        }
      } else {
        showToast("An unknown error occurred.", { status: "error" });
      }
    },
  });
  const [open2FA, setOpen2FA] = useState(false);
  const [method2FA, setMethod2FA] = useState("");
  const [contact2FA, setContact2FA] = useState("");

  const { colorMode } = useColorMode();


  const handleSubmit = async(e) => {
    e.preventDefault();
    await dispatch(authenticate({ email, password }));
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    await dispatch(googleSSO({ credential: credentialResponse.credential }));
  };

  const handleGoogleError = () => {
    showToast("Google sign-in failed", {
      description: "Could not complete Google sign-in. Please try again or use email/password.",
      status: "error",
      duration: 5000,
    });
  };

  useEffect(() => {
    evaluateSuccess();
  }, [success]); 

  const evaluateSuccess = async () => {
    if (success === true && user?.result?.includes("2FA Enabled, Verification Code Sent to Email")) {
      setOpen2FA(true);
      setMethod2FA('email');
      setContact2FA(user.result.slice(user.result.lastIndexOf(" ") + 1));
    }else if (success === true && user?.result?.includes("2FA Enabled, Verification Code Sent to Phone Number")) {
      setOpen2FA(true);
      setMethod2FA('phone number');
      setContact2FA(user.result.slice(user.result.lastIndexOf(" ") + 1));
    }
  }



  const handleNewCode = async(e) => {
    e.preventDefault();
    await dispatch(authenticate({ email, password }));
  };

  const handleCancel = async(e) => {
    e.preventDefault();
    setOpen2FA(false);
    setVerificationCode("");
    setEmail("");
    setPassword("");
  };

  const handleSubmit2FA = async(e) => {
    e.preventDefault();
    let code = verificationCode
    await dispatch(confirm2FA({ email, code }));
    setVerificationCode("");
  };
 

  return (
    <Flex
      direction="column"
      minH="100vh"
      bg={colorMode === 'light' ? "gray.200" : "gray.800"}
      justifyContent="center"
      alignItems="center"
    >
      <Stack
        flexDir="column"
        mb="2"
        justifyContent="center"
        alignItems="center"
      >
        {!open2FA ?
          <>
          <Box minW={{ base: "90%", md: "468px" }} rounded="lg" overflow="hidden" boxShadow="2px 4px 4px rgba(0,0,0,0.2)">
          {authLoading ?
            <Stack
                spacing={4}
                p="1rem"
                backgroundColor={colorMode === 'light' ? "whiteAlpha.900" : "whiteAlpha.100"}
                boxShadow="md"
              >
              <Center>
              <Spinner
                  thickness='4px'
                  speed='0.65s'
                  emptyColor='gray.200'
                  color='blue.500'
                  size='xl'
              />
              </Center>
            </Stack>
          :
            <form>
              <Stack
                spacing={3}
                p="1rem"
                backgroundColor={colorMode === 'light' ? "whiteAlpha.900" : "whiteAlpha.100"}
                boxShadow="md"
              >
                <Link href="/" color="blue.500" fontSize="sm" ml={1} mb={-1}>
                  ← Back to home
                </Link>
                <Heading className="page-heading" fontSize="xl" ml={1} mb={-1} color={colorMode === 'light' ? 'gray.600' : 'gray.200'}>Login</Heading>
                <FormControl>
                  <InputGroup>
                    <InputLeftElement
                      pointerEvents="none"
                      children={<CFaUserAlt color="gray.300" />}
                    />
                    <Input
                      type="email"
                      placeholder="email address"
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </InputGroup>
                </FormControl>
                <FormControl>
                  <InputGroup>
                    <InputLeftElement
                      pointerEvents="none"
                      color="gray.300"
                      children={<CFaLock color="gray.300" />}
                    />
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder="Password"
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <InputRightElement width="4.5rem">
                      <Button
                        variant="ghost"
                        onClick={() => setShowPassword((prev) => !prev)}
                      >
                        {showPassword ? <ViewIcon /> : <ViewOffIcon />}
                      </Button>
                    </InputRightElement>
                  </InputGroup>
                  <Box display="flex" justifyContent="right" pt={1}>
                    <Link color="blue.500" href="/resetpassword" fontSize="sm">
                      Forgot password?
                    </Link>
                  </Box>
                </FormControl>
                <Button
                  borderRadius="md"
                  type="submit"
                  variant="solid"
                  colorScheme="blue"
                  width="full"
                  onClick={handleSubmit}
                >
                  Login
                </Button>
                <HStack spacing={2}>
                  <Divider />
                  <Text fontSize="xs" color="gray.500" whiteSpace="nowrap">or</Text>
                  <Divider />
                </HStack>
                <Center>
                  <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    onError={handleGoogleError}
                    text="signin_with"
                    width="300"
                  />
                </Center>
                <Text textAlign="center" fontSize="sm">
                  Don't have an account?{" "}
                  <Link color="blue.500" href="/signup" onClick={() => window.localStorage.removeItem('userInfo')}>
                    Sign up
                  </Link>
                </Text>
              </Stack>
            </form>
          }
          </Box>
          </>
        :
          <>
          <Box minW={{ base: "90%", md: "468px" }} rounded="lg" overflow="hidden" boxShadow="2px 4px 4px rgba(0,0,0,0.2)">
          {authLoading ?
            <Stack
                spacing={4}
                p="1rem"
                backgroundColor={colorMode === 'light' ? "whiteAlpha.900" : "whiteAlpha.100"}
                boxShadow="md"
              >
              <Center>
              <Spinner
                  thickness='4px'
                  speed='0.65s'
                  emptyColor='gray.200'
                  color='blue.500'
                  size='xl'
              />
              </Center>
            </Stack>
          :
            <form>
              <Stack
                spacing={3}
                p="1rem"
                backgroundColor={colorMode === 'light' ? "whiteAlpha.900" : "whiteAlpha.100"}
                boxShadow="md"
              >
                <Heading className="page-heading" fontSize="xl" ml={1} mb={-1} color={colorMode === 'light' ? 'gray.600' : 'gray.200'}>Login - 2FA</Heading>
                <Center>
                  <Text>
                    Two-Factor Code sent to {method2FA}:&nbsp;
                    <b>{contact2FA}</b>
                  </Text>
                </Center>
                <FormControl>
                  <InputGroup>
                    <InputLeftElement
                      pointerEvents="none"
                      color="gray.300"
                      children={<CFaLock color="gray.300" />}
                    />
                    <Input
                      type="text"
                      placeholder="Enter Verification Code"
                      onChange={(e) => setVerificationCode(e.target.value)}
                    />
                  </InputGroup>
                </FormControl>
                <Center>
                  <Text>
                    Didn't get a code?&nbsp;
                  </Text>
                  <Link color="blue.500" onClick={handleNewCode}>
                    Send Another
                  </Link>
                </Center>
                <Button
                  borderRadius="md"
                  type="submit"
                  variant="solid"
                  colorScheme="blue"
                  width="full"
                  onClick={handleSubmit2FA}
                >
                  Submit
                </Button>
                <Button
                  borderRadius="md"
                  type="submit"
                  variant="solid"
                  colorScheme="blue"
                  width="full"
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
              </Stack>
            </form>
          }
          </Box>
          </>
        }
      </Stack>
    </Flex>
  );
}